import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { ButtonComponent } from 'app/shared/components/button/button.component';
import { IconComponent } from 'app/shared/components/icons/icon/icon.component';
import { NavTab, NavTabComponent } from 'app/shared/components/nav-tab/nav-tab.component';
import { SkeletonBlockComponent } from 'app/shared/components/skeleton/skeleton-block.component';
import { FormatPipe } from 'app/shared/pipes/format.pipe';

import { NotificationItemComponent } from '../../components/notification-item/notification-item.component';
import { NotificationGroup, NotificationTab, NotificationView } from '../../models/notification-view';

export type { NotificationTab } from '../../models/notification-view';
export type NotificationCenterPresentation = 'page' | 'popover';

@Component({
    selector: 'app-notification-center',
    templateUrl: './notification-center.page.html',
    styleUrl: './notification-center.page.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.notification-center-popover]': 'isPopover()',
    },
    imports: [
        ButtonComponent,
        FormatPipe,
        IconComponent,
        NavTabComponent,
        NotificationItemComponent,
        RouterLink,
        SkeletonBlockComponent,
        TranslatePipe,
    ],
})
export class NotificationCenterPage {
    private readonly translate = inject(TranslateService);
    private readonly langChange = toSignal(this.translate.onLangChange);

    readonly unread = input<NotificationGroup[]>([]);
    readonly read = input<NotificationGroup[]>([]);
    readonly tab = input<NotificationTab>('unread');
    readonly presentation = input<NotificationCenterPresentation>('page');
    readonly loading = input(false);
    readonly error = input(false);
    readonly markingAllAsRead = input(false);

    readonly markAllAsRead = output<void>();
    readonly open = output<NotificationView>();
    readonly openCenter = output<void>();
    readonly retry = output<void>();
    readonly tabChange = output<NotificationTab>();

    protected readonly skeletonGroups = [[0, 1], [0], [0]];
    protected readonly isPopover = computed(() => this.presentation() === 'popover');

    protected readonly titleKey = computed(() => (this.isPopover() ? 'MESSAGES' : 'NOTIFICATION_CENTER_TITLE'));

    protected readonly unreadCount = computed(() =>
        this.unread().reduce((total, group) => total + group.notifications.length, 0),
    );

    protected readonly showMarkAllButton = computed(() => this.tab() === 'unread');

    protected readonly emptyMessageKey = computed(() =>
        this.tab() === 'unread' ? 'NOTIFICATION_CENTER_EMPTY_UNREAD' : 'NOTIFICATION_CENTER_EMPTY_READ',
    );

    protected readonly groups = computed<NotificationGroup[]>(() =>
        this.tab() === 'unread' ? this.unread() : this.read(),
    );

    protected readonly tabs = computed<NavTab[]>(() => {
        this.langChange();
        const unreadCount = this.unreadCount();

        return [
            {
                label: this.translate.instant('NOTIFICATION_CENTER_TAB_UNREAD'),
                chip: unreadCount,
                active: this.tab() === 'unread',
                ...(this.isPopover()
                    ? { onClick: () => this.tabChange.emit('unread') }
                    : { routerLink: ['/notifications', 'unread'] }),
            },
            {
                label: this.translate.instant('NOTIFICATION_CENTER_TAB_READ'),
                active: this.tab() === 'read',
                ...(this.isPopover()
                    ? { onClick: () => this.tabChange.emit('read') }
                    : { routerLink: ['/notifications', 'read'] }),
            },
        ];
    });
}
